import {
	Conversation,
	InShort,
	Prose,
	RelatedPages,
	type Turn,
} from "@marketing/components/answer";
import {
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
	Steps,
} from "@marketing/components/blocks";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "What does AI do for quick commerce?",
		a: "It shortens the path from wanting something to a confirmed basket. A shopper types or says what they need in one message, and the assistant searches the stock of the dark store or branch that will deliver, fills the basket and builds the checkout for the shopper to confirm.",
	},
	{
		q: "Does it see each dark store's stock?",
		a: "Yes. Each dark store or branch is its own store with its own prices and stock, and the assistant only offers what that store can deliver. Out-of-stock products cannot be added.",
	},
	{
		q: "Can shoppers reorder their usual items?",
		a: "If the retailer turns on memory, the assistant asks a shopper the retailer has identified once whether it may remember things such as the products they buy regularly. Only a yes saves anything, and saying forget clears it.",
	},
	{
		q: "What happens when an item runs out before packing?",
		a: "When the retailer turns it on, the assistant asks the shopper once what to do with an unavailable item: a similar replacement, leave it out, or a specific product. The store can set its own default.",
	},
	{
		q: "Can shoppers order by WhatsApp or phone?",
		a: "Yes. On your WhatsApp Business number with product cards and a checkout confirmed with a tap, by voice note, and on WhatsApp calls or phone calls where the basket is read back and confirmed on the keypad.",
	},
	{
		q: "How fast does it answer on calls?",
		a: "Replies on calls come in under a second. The call service starts the product search while the caller is still finishing their sentence.",
	},
];

const SAMPLE: Turn[] = [
	{ from: "shopper", text: "milk, bread and eggs" },
	{
		from: "assistant",
		text: "Which specific brand or type of milk, bread, and eggs would you like to add?",
		note: "Searched the three items in one step and showed 24 matches, 8 per item, from $1.00 pita to organic eggs.",
	},
];

const SPEED = [
	{
		title: "Ordering in a sentence",
		text: 'A whole list in one message, a voice note, or "the cheapest one". No browsing three aisles for three items.',
	},
	{
		title: "The usual, remembered",
		text: 'With the shopper\'s consent, it remembers the products they buy regularly, so "my usual" is a short request.',
	},
	{
		title: "Stock per dark store",
		text: "Only what the store that will deliver has in stock, at its price.",
	},
	{
		title: "Substitutions decided early",
		text: "Out-of-stock items cannot be added, and the shopper can say up front what to do if one runs out at packing.",
	},
	{
		title: "WhatsApp and in-app",
		text: "The same basket in your app and on your WhatsApp Business number.",
	},
	{
		title: "Calls in under a second",
		text: "Voice orders by phone and WhatsApp call, read back and confirmed on the keypad.",
	},
];

const LIVE = [
	{
		title: "Connect each store",
		text: "Every dark store or branch with its own stock and prices, from your platform or our API.",
	},
	{
		title: "Test on your catalog",
		text: "Requests generated from your products, in your shoppers' languages, run before launch.",
	},
	{
		title: "Open your channels",
		text: "In your app, on WhatsApp, and on calls when you are ready.",
	},
];

export const Route = createFileRoute("/_marketing/quick-commerce")({
	head: () =>
		marketingHead({
			title: "AI for Quick Commerce and Grocery Delivery | Hephon Commerce",
			description:
				"AI for quick commerce: shoppers order in a sentence, the assistant uses each dark store's stock, handles substitutions and orders on WhatsApp and calls.",
			path: "/quick-commerce",
			breadcrumb: ["Quick commerce"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: QuickCommercePage,
});

function QuickCommercePage() {
	return (
		<>
			<PageIntro
				kicker="Quick commerce"
				title="AI for quick commerce: from a sentence to a confirmed basket"
				lead="Shoppers say what they need in one message. The assistant checks the dark store that will deliver, fills the basket and hands over a checkout to confirm."
			/>

			<InShort updated={UPDATED}>
				<p>
					Hephon Commerce is an AI shopping assistant for quick
					commerce and grocery delivery apps. A shopper types, sends a
					voice note or calls with what they need, and it searches the
					stock of the dark store or branch that will deliver, fills
					the basket and builds a checkout the shopper confirms.
				</p>
				<p>
					It runs inside your app, on your WhatsApp Business number
					and on calls, in Arabic, Arabizi, English and French.
				</p>
			</InShort>

			<Section
				id="speed-title"
				title="Where does AI save time in quick commerce?"
				lead="Delivery takes minutes. Building the basket should not take longer than the ride."
			>
				<DetailGrid items={SPEED} />
			</Section>

			<Section
				id="sentence-title"
				tone="mist"
				title="How does ordering in a sentence work?"
			>
				<Prose>
					<p>
						Quick commerce shoppers usually know what they want. The
						slow part is the app: search one item, scroll, add,
						search the next. With Hephon Commerce the shopper writes
						the list as they would text a friend, "milk, bread and
						eggs", and the assistant searches every item in one step
						and shows a row of choices for each.
					</p>
					<p>
						It understands the shortcuts people use: "the cheapest
						one", "more options", "something similar", "any of these
						on offer?", a brand name in Arabic for a product listed
						in English, Arabizi like "7aleeb" for milk. Each
						search's top results are checked against what the
						shopper actually asked before they are shown.
					</p>
				</Prose>
				<div className="mt-12">
					<Conversation
						turns={SAMPLE}
						caption="Captured from the live demo store, Hephon Market, on 5 November 2026; prices in US dollars from the demo catalog."
					/>
				</div>
			</Section>

			<Section
				id="stock-title"
				title="How does it handle dark-store stock and substitutions?"
			>
				<Prose>
					<p>
						Each dark store or branch is its own store in Hephon
						Commerce, with its own stock and prices. The shopper is
						only offered what the store that will deliver has, and
						out-of-stock products cannot be added to the basket on
						any channel. If a shopper asks for one by name, the
						assistant says it is out of stock and offers something
						in stock instead.
					</p>
					<p>
						Stock still changes between the order and the packing.
						When the retailer turns it on, the assistant asks the
						shopper once, after adding to the basket, what to do if
						an item is unavailable at packing time: a similar
						replacement, leave it out, or a specific product. The
						store can set its default, and the answer is recorded
						with the basket.
					</p>
					<p>
						On WhatsApp, the assistant can ask where the shopper is
						before it shows any prices, so the basket is built from
						the right store from the first message. If the shopper
						changes location, the basket moves with them and the
						assistant offers substitutes for items the new store
						does not stock.
					</p>
				</Prose>
			</Section>

			<Section
				id="channels-title"
				tone="mist"
				title="Which channels can shoppers order on?"
			>
				<Prose>
					<p>
						<strong>In your app.</strong> An SDK for iOS, Android
						and the web, with React Native and Flutter. The
						assistant's basket is in sync with your app's cart, so
						the shopper checks out the way they always do.
					</p>
					<p>
						<strong>On WhatsApp.</strong> Your WhatsApp Business
						number answers with product cards, builds the basket and
						sends a checkout the shopper confirms with a tap. Voice
						notes are understood too.
					</p>
					<p>
						<strong>On calls.</strong> Shoppers can call your
						WhatsApp number, or talk live inside your app, and order
						by voice. The call service listens before it
						transcribes, ignores clips with no speech, notices when
						a sentence stopped half way, and starts the product
						search while the caller is still finishing. Replies come
						in under a second. Before anything is ordered the basket
						is read back, and the caller presses 1 to confirm. A
						caller can always be handed to a person.
					</p>
				</Prose>
			</Section>

			<Section id="fit-title" title="What it does not do">
				<Prose>
					<ul>
						<li>
							<strong>No order without a confirm.</strong> It
							never places an order on a typed or spoken yes
							alone.
						</li>
						<li>
							<strong>No rider or warehouse tools.</strong> It
							does not dispatch drivers, plan routes or manage
							picking. Orders go to your platform or your team.
						</li>
						<li>
							<strong>No memory without consent.</strong> Usual
							items are remembered only if you turn memory on and
							the shopper agrees.
						</li>
						<li>
							<strong>No self-serve sign-up.</strong> Access is by
							invitation, after a demo on your catalog.
						</li>
					</ul>
				</Prose>
			</Section>

			<Section
				id="live-title"
				tone="mist"
				title="Live in three steps."
				center
			>
				<Steps items={LIVE} />
			</Section>

			<Faq items={FAQ} title="Quick commerce, answered." />
			<RelatedPages
				links={[
					{ href: "/supermarkets", label: "AI for supermarkets" },
					{ href: "/channels/whatsapp", label: "WhatsApp shopping" },
					{ href: "/channels/voice", label: "Voice ordering" },
					{ href: "/channels/in-app", label: "In-app assistant" },
					{ href: "/shoppable-recipes", label: "Shoppable recipes" },
					{ href: "/uae", label: "UAE" },
				]}
			/>
			<Closing />
		</>
	);
}
